import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { FormField, Input, Select } from "../UI/FormField";
import { Task, TaskStatus, TaskType } from "../../types/tasks";
import { TASK_STATUSES, TASK_TYPES, TASK_STATUS_COLORS } from "../../constants/taskConstants";
import SearchableSelect, { type SelectOption } from "../UI/SearchableSelect";
import api from "../../services/api";

interface Props {
  value: Partial<Task>;
  onChange: (patch: Partial<Task>) => void;
  errors: Partial<Record<keyof Task, string>>;
  readonly?: boolean;
  clientLocked?: boolean;
  assigneeLocked?: boolean;
}

const RVal: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <span className="text-[13px] text-gray-800 leading-5">
    {children ?? <span className="text-gray-400">—</span>}
  </span>
);

/**
 * Hybrid client field: user can type a free-text company name OR select from the
 * partners dropdown. Selecting from the dropdown stores both clientId and clientName.
 * Typing without selecting stores only clientName (clientId is cleared).
 */
const ClientCombobox: React.FC<{
  clientId?: string;
  clientName?: string;
  onChange: (clientId: string | undefined, clientName: string | undefined) => void;
  error?: boolean;
  disabled?: boolean;
}> = ({ clientId, clientName, onChange, error, disabled }) => {
  const { t } = useTranslation("tasks");
  const [inputValue, setInputValue] = useState(clientName ?? "");
  const [suggestions, setSuggestions] = useState<SelectOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep input in sync when value changes externally
  useEffect(() => {
    setInputValue(clientName ?? "");
  }, [clientName]);

  const fetchSuggestions = useCallback(async (search: string) => {
    if (!search.trim()) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await api.get("/partners", { params: { search: search.trim(), page: 1, limit: 8 } });
      const partners: { _id: string; companyName: string }[] = res.data.data?.partners ?? [];
      setSuggestions(partners.map((p) => ({ value: p._id, label: p.companyName })));
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    // Free-text: clear the clientId since this is no longer a selected partner
    onChange(undefined, val || undefined);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (opt: SelectOption) => {
    setInputValue(opt.label);
    onChange(opt.value, opt.label);
    setSuggestions([]);
    setOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    onChange(undefined, undefined);
    setSuggestions([]);
    setOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className={`relative flex items-center border rounded-md bg-white transition-colors ${
        error ? "border-red-400" : "border-gray-300 focus-within:border-primary"
      } ${disabled ? "opacity-60 bg-gray-50" : ""}`}>
        <Search className="w-3.5 h-3.5 text-gray-400 ml-2 shrink-0" />
        <input
          type="text"
          className="flex-1 px-2 py-1.5 text-[13px] bg-transparent outline-none placeholder-gray-400 disabled:cursor-not-allowed"
          placeholder={t("form.clientPlaceholder")}
          value={inputValue}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={() => { if (inputValue) fetchSuggestions(inputValue); setOpen(true); }}
        />
        {/* Show a small "from DB" badge when an ID is linked */}
        {clientId && (
          <span className="text-[10px] text-blue-600 bg-blue-50 border border-blue-200 rounded px-1 mr-1 shrink-0 whitespace-nowrap">
            Partner
          </span>
        )}
        {inputValue && !disabled && (
          <button type="button" onClick={handleClear} className="p-1 text-gray-400 hover:text-gray-600 mr-0.5">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (inputValue || suggestions.length > 0) && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading && (
            <div className="px-3 py-2 text-[12px] text-gray-400">Searching…</div>
          )}
          {!loading && suggestions.length === 0 && inputValue.trim() && (
            <div className="px-3 py-2 text-[12px] text-gray-400">
              No partners found — will use "{inputValue}" as free text
            </div>
          )}
          {suggestions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className="w-full text-left px-3 py-2 text-[13px] hover:bg-gray-50 flex items-center gap-2"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
            >
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const TaskFormGeneral: React.FC<Props> = ({
  value,
  onChange,
  errors,
  readonly,
  clientLocked,
  assigneeLocked,
}) => {
  const { t } = useTranslation("tasks");

  const fetchAssignees = useCallback(
    async (search: string): Promise<{ options: SelectOption[]; hasMore: boolean }> => {
      try {
        const res = await api.get("/tasks/assignees");
        const users: { _id: string; firstName: string; lastName: string }[] =
          res.data.data?.users ?? [];
        const q = search.trim().toLowerCase();
        const filtered = q
          ? users.filter((u) =>
              `${u.firstName} ${u.lastName}`.toLowerCase().includes(q),
            )
          : users;
        return {
          options: filtered.map((u) => ({
            value: u._id,
            label: `${u.firstName} ${u.lastName}`.trim(),
          })),
          hasMore: false,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [],
  );

  /* ── View mode: plain text ──────────────────────────────────────────────── */
  if (readonly) {
    return (
      <div className="space-y-1">
        <FormField layout="inline" size="sm" label={t("form.type")}>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            value.type === "todo" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"
          }`}>
            {value.type ? t(`form.typeOptions.${value.type}`) : "—"}
          </span>
        </FormField>

        <FormField layout="inline" size="sm" label={t("form.description")}>
          <RVal>{value.description}</RVal>
        </FormField>

        <FormField layout="inline" size="sm" label={t("form.client")}>
          <RVal>{value.clientName}</RVal>
        </FormField>

        <FormField layout="inline" size="sm" label={t("form.useStructuredForm")}>
          <RVal>{value.useStructuredForm ? "Yes" : "No"}</RVal>
        </FormField>

        <FormField layout="inline" size="sm" label={t("form.assign")}>
          <RVal>{value.assigneeName}</RVal>
        </FormField>

        <FormField layout="inline" size="sm" label={t("form.status")}>
          {value.status ? (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${TASK_STATUS_COLORS[value.status as TaskStatus]}`}>
              {t(`form.statusOptions.${value.status}`)}
            </span>
          ) : <RVal />}
        </FormField>
      </div>
    );
  }

  /* ── Edit mode ──────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-1">
      {/* Type toggle */}
      <FormField layout="inline" size="sm" label={t("form.type")} required error={errors.type}>
        <div className="inline-flex rounded-md border border-gray-300 bg-white overflow-hidden h-8">
          {TASK_TYPES.map((ty) => {
            const active = value.type === ty;
            return (
              <button
                key={ty}
                type="button"
                onClick={() => onChange({ type: ty as TaskType })}
                className={`px-3 text-[13px] font-medium transition-colors ${
                  active ? "bg-primary text-white" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {t(`form.typeOptions.${ty}`)}
              </button>
            );
          })}
        </div>
      </FormField>

      {/* Description */}
      <FormField layout="inline" size="sm" label={t("form.description")} required error={errors.description}>
        <Input
          type="text"
          size="sm"
          value={value.description ?? ""}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder={t("form.descriptionPlaceholder")}
          error={!!errors.description}
        />
      </FormField>

      {/* Client — hybrid: free text or select from dropdown */}
      <FormField layout="inline" size="sm" label={t("form.client")} error={errors.clientId}>
        <ClientCombobox
          clientId={value.clientId}
          clientName={value.clientName}
          error={!!errors.clientId}
          disabled={clientLocked}
          onChange={(id, name) => onChange({ clientId: id || undefined, clientName: name || undefined })}
        />
      </FormField>

      {/* Use structured form — disabled when locked in edit mode */}
      <FormField layout="inline" size="sm" label="">
        <label className={`flex items-center gap-2 flex-wrap select-none ${clientLocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
          <input
            type="checkbox"
            checked={value.useStructuredForm ?? false}
            disabled={clientLocked}
            onChange={(e) => onChange({ useStructuredForm: e.target.checked })}
            className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed"
          />
          <span className="text-[12px] font-medium text-gray-700 whitespace-nowrap">{t("form.useStructuredForm")}</span>
          {!clientLocked && (
            <span className="hidden sm:inline text-[11px] text-gray-400">· {t("form.useStructuredFormHint")}</span>
          )}
        </label>
      </FormField>

      {/* Assignee — disabled for non-admin users */}
      <FormField layout="inline" size="sm" label={t("form.assign")} required error={errors.assigneeId}>
        <SearchableSelect
          value={value.assigneeId ?? ""}
          displayValue={value.assigneeName}
          placeholder={t("form.assignPlaceholder")}
          fetchOptions={fetchAssignees}
          error={!!errors.assigneeId}
          disabled={assigneeLocked}
          onChange={(id, label) =>
            onChange({ assigneeId: id || undefined, assigneeName: label || undefined })
          }
        />
      </FormField>

      {/* Status */}
      <FormField layout="inline" size="sm" label={t("form.status")}>
        <Select
          size="sm"
          value={value.status ?? "new"}
          onChange={(e) => onChange({ status: e.target.value as TaskStatus })}
        >
          {TASK_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`form.statusOptions.${s}`)}</option>
          ))}
        </Select>
      </FormField>
    </div>
  );
};
